import React, {
  useCallback, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {hooks} from 'farmx-redux-core';
import { AIR_TEMPERATURE } from 'components/hooks/graph';
import Moment from 'moment';
import {
  convertToCelsius,
  getYminMax
} from '../../helper/graphHelper';
import NewSensorDataChart from './NewSensorDataChart';
import { VAR_AIR_TEMPERATURE } from './constants';

const { useUnits } = hooks;

export default function AirTemperatureChart(props) {
  const {
    sensor, startDate, endDate, cached, updateYminmax, uniformYAxis, yMinMaxMap
  } = props;
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const getUserUnits = useUnits();

  const globalYMinMax = yMinMaxMap && yMinMaxMap[AIR_TEMPERATURE]
    ?yMinMaxMap[AIR_TEMPERATURE]:{min: null, max: null};

  const yMinMax = useMemo(() => {
    if((globalYMinMax.min || globalYMinMax.min===0)
      && globalYMinMax.max) { return { min: globalYMinMax.min, max: globalYMinMax.max}; }
    return null;
  }, [globalYMinMax.min, globalYMinMax.max]);

  const customConfig = useCallback((configObj, seriesData) => {
    if(configObj?.series?.[0]){
      const yMinMax = getYminMax(seriesData);
      const { multiplier } = configObj?.series?.[0];
      updateYminmax(AIR_TEMPERATURE, yMinMax.min * multiplier, yMinMax.max * multiplier);
      const {value: yMin, label} = convertToCelsius(yMinMax.min * multiplier, getUserUnits);
      const {value: yMax} = convertToCelsius(yMinMax.max * multiplier, getUserUnits);
      setLabel(label);
      return {
        ...configObj,
        series: [{
          ...configObj?.series?.[0],
          tooltip: {
            valueDecimals: 1,
            valueSuffix: t(` ${label}`),
          },
          name: t('Air Temperature'),
        }],
        yAxis: [{
          ...configObj.yAxis[0],
          min: yMin,
          max: yMax
        }]
      };
    }
    return configObj;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateYminmax]);

  return (
    <>
      <NewSensorDataChart
        sensorName={sensor.name}
        sensorId={sensor.id}
        sensorType={sensor.type}
        sensorIdentifier={sensor.identifier}
        variables={VAR_AIR_TEMPERATURE}
        startDate={startDate}
        endDate={endDate}
        cached={cached}
        customConfig={customConfig}
        header={{title: t('Air Temperature'), units: t(label), right: []}}
        uniformYAxis={uniformYAxis}
        yMinMax={yMinMax}
      />
    </>
  );
}

AirTemperatureChart.propTypes = {
  sensor: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    type: PropTypes.string,
    identifier: PropTypes.string,
  }),
  startDate: PropTypes.instanceOf(Moment),
  endDate: PropTypes.instanceOf(Moment),
  cached: PropTypes.string,
  updateYminmax: PropTypes.func,
  uniformYAxis: PropTypes.bool,
  yMinMaxMap: PropTypes.shape({}),
};

AirTemperatureChart.defaultProps = {
  sensor: null,
  startDate: null,
  endDate: null,
  cached: null,
  updateYminmax: () => null,
  uniformYAxis: false,
  yMinMaxMap: null,
};
